import { create } from 'zustand';

/**
 * Free-form metadata that describes the designed structure at the level
 * of a real-world dossier. Unlike `useConstraintsStore`, this state is
 * intentionally kept OUT of the generative pipeline — nothing here
 * influences geometry. It is, however, serialised into every export
 * (USDA, GLB) so the downstream pipeline can attribute the asset.
 *
 * The fields are deliberately small and free-form: we don't try to
 * validate an address or pin a description to any schema. The user
 * types whatever they want; we write it out verbatim.
 */
export interface MetadataState {
  /** Street address or free-form location ("123 Elm St" or "Pacific NW demo"). */
  siteAddress: string;
  /** One-paragraph description ("Modern 2-story family home, south-facing …"). */
  siteDescription: string;
  setSiteAddress(value: string): void;
  setSiteDescription(value: string): void;
}

const STORAGE_KEY = 'structure.metadata.v1';

interface PersistedMetadata {
  siteAddress?: string;
  siteDescription?: string;
}

function loadInitial(): Pick<MetadataState, 'siteAddress' | 'siteDescription'> {
  if (typeof window === 'undefined' || !window.localStorage) {
    return { siteAddress: '', siteDescription: '' };
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { siteAddress: '', siteDescription: '' };
    const parsed = JSON.parse(raw) as PersistedMetadata;
    return {
      siteAddress: typeof parsed.siteAddress === 'string' ? parsed.siteAddress : '',
      siteDescription: typeof parsed.siteDescription === 'string' ? parsed.siteDescription : '',
    };
  } catch {
    return { siteAddress: '', siteDescription: '' };
  }
}

function persist(state: Pick<MetadataState, 'siteAddress' | 'siteDescription'>): void {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        siteAddress: state.siteAddress,
        siteDescription: state.siteDescription,
      } satisfies PersistedMetadata),
    );
  } catch {
    // Quota / private mode — we silently accept loss of persistence.
  }
}

export const useMetadataStore = create<MetadataState>((set, get) => ({
  ...loadInitial(),
  setSiteAddress(value) {
    set({ siteAddress: value });
    persist(get());
  },
  setSiteDescription(value) {
    set({ siteDescription: value });
    persist(get());
  },
}));
